import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  BookOpen,
  Users,
  Briefcase,
  CheckSquare,
  MessageSquare,
  Zap,
  Mail,
  BarChart3,
  Settings,
  LogIn,
  HelpCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"

type Section = {
  id: string
  title: string
  icon: typeof BookOpen
  content: React.ReactNode
}

const SECTIONS: Section[] = [
  {
    id: "overview",
    title: "Overview",
    icon: BookOpen,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p>
          Welcome to the Rivercity Finance CRM. This system helps brokers manage contacts, deals,
          tasks, communications and documents from one place.
        </p>
        <p>
          It runs entirely on Cloudflare — no separate hosting, no PHP. Every action is audited, and
          all data is scoped to your organisation with role-based access.
        </p>
      </div>
    ),
  },
  {
    id: "getting-started",
    title: "Getting Started",
    icon: LogIn,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <ol className="ml-5 list-decimal space-y-2">
          <li>
            Sign in with your email + password. On first login you'll be asked to enrol in{" "}
            <strong>two-factor authentication</strong> using an authenticator app (Google
            Authenticator, Authy, 1Password, etc.).
          </li>
          <li>
            Save the recovery codes shown after enrolment. They're the only way in if you lose your
            authenticator device.
          </li>
          <li>
            From <strong>My Settings</strong>, connect your Outlook mailbox so incoming emails
            appear on the correct contact.
          </li>
        </ol>
      </div>
    ),
  },
  {
    id: "contacts",
    title: "Contacts",
    icon: Users,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p>Contacts are the people (and businesses) you work with.</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Add a new contact from the <strong>Contacts</strong> tab or the sidebar.</li>
          <li>Type an ABN and click <strong>Lookup</strong> to auto-fill business name and state from the ABR.</li>
          <li>Duplicates are detected by email, mobile or ABN — you'll be warned before you can create a second copy.</li>
          <li>The contact card shows all deals, tasks, communications and documents in one place.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "deals",
    title: "Deals & Pipeline",
    icon: Briefcase,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p>A deal is a finance enquiry linked to a contact.</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Create a deal from a contact card or the <strong>New Deal</strong> button in the topbar.</li>
          <li>The <strong>Pipeline</strong> view is a drag-and-drop board — grab a card and drop it in the next stage.</li>
          <li>Every stage change is logged in the deal's history and audited.</li>
          <li>Deal stages track follow-up cadence (Day 1, Day 2, etc.) as well as canonical status (Awaiting Docs, Docs Received…).</li>
        </ul>
      </div>
    ),
  },
  {
    id: "tasks",
    title: "Tasks & Follow-ups",
    icon: CheckSquare,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <ul className="ml-5 list-disc space-y-2">
          <li>Tasks can be attached to a contact, a deal, or neither.</li>
          <li>Filter views: <em>My Tasks</em>, <em>Today</em>, <em>Overdue</em>, <em>Upcoming 7 Days</em>, <em>Completed</em>, <em>All</em>.</li>
          <li>Tasks created by automations are tagged with <em>Automation</em>.</li>
          <li>Overdue count appears on the dashboard as a red badge.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "communications",
    title: "Communications",
    icon: MessageSquare,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p>Every contact card has a <strong>Communication Timeline</strong> showing:</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Notes typed manually (quick add at top of the timeline)</li>
          <li>Calls logged automatically from Aircall (once connected in Settings)</li>
          <li>Emails matched from your Outlook inbox (once you connect in My Settings)</li>
          <li>Stage changes and automation runs as system activity</li>
        </ul>
      </div>
    ),
  },
  {
    id: "automations",
    title: "Automations",
    icon: Zap,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p>Automations run deterministic workflows triggered by CRM events.</p>
        <p>
          You'll write them in plain English (e.g. <em>"When a new web lead is assigned to a
          broker, send an SMS after 5 minutes and create a call task"</em>) and the CRM will convert
          them into a structured workflow you can review, edit and activate.
        </p>
        <p className="text-muted-foreground text-xs italic">Coming in a future release.</p>
      </div>
    ),
  },
  {
    id: "templates",
    title: "Templates",
    icon: Mail,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <ul className="ml-5 list-disc space-y-2">
          <li>Save reusable email and SMS templates.</li>
          <li>Use merge fields like <code>{"{{first_name}}"}</code>, <code>{"{{loan_amount}}"}</code>, <code>{"{{broker_name}}"}</code>.</li>
          <li>Set an HTML email signature from the template editor.</li>
          <li>Share templates with the team or keep them private.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "reports",
    title: "Reports",
    icon: BarChart3,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p>Reports pull live figures from your deal data — no fake numbers.</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Settled this month, last month, and pipeline value</li>
          <li>90-day conversion rate</li>
          <li>Broker performance (last 3 months)</li>
          <li>Top finance categories</li>
          <li>12-month settled trend chart</li>
        </ul>
        <p>Filter by broker or finance type.</p>
      </div>
    ),
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p><strong>My Settings</strong> (all users): personal Outlook connection.</p>
        <p><strong>Settings</strong> (admin only): organisation integrations (Aircall), user management (coming), API keys (coming).</p>
      </div>
    ),
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: HelpCircle,
    content: (
      <div className="space-y-3 text-sm text-foreground">
        <p><strong>I lost my authenticator device.</strong> Use a recovery code from your saved list. If you've used them all, ask an admin to reset your TOTP.</p>
        <p><strong>An email isn't showing on the contact card.</strong> Check the Unmatched Emails queue in My Settings — the sender email may not match any contact yet.</p>
        <p><strong>A call didn't log to a contact.</strong> The phone number couldn't be matched. Add the number to the contact and the next call will link automatically.</p>
      </div>
    ),
  },
]

export default function UserManualPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeId = searchParams.get("section") ?? SECTIONS[0]!.id
  const [q, setQ] = useState("")

  const filtered = q
    ? SECTIONS.filter((s) => s.title.toLowerCase().includes(q.toLowerCase()))
    : SECTIONS

  const active = SECTIONS.find((s) => s.id === activeId) ?? SECTIONS[0]!

  return (
    <AppShell title="User Manual" subtitle="How the Rivercity CRM works">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Sidebar TOC */}
        <div className="lg:col-span-1">
          <Card className="p-3">
            <input
              type="search"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border-input mb-3 flex h-9 w-full rounded-md border bg-background px-3 text-sm"
            />
            <nav>
              {filtered.map((s) => {
                const Icon = s.icon
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams)
                      next.set("section", s.id)
                      setSearchParams(next)
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                      s.id === activeId
                        ? "bg-rc-blue-600 text-white"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{s.title}</span>
                  </button>
                )
              })}
            </nav>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <Card className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-rc-blue-50 text-rc-blue-600 grid h-10 w-10 place-items-center rounded-lg">
                <active.icon className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">{active.title}</h2>
            </div>
            <div className="prose prose-sm max-w-none">{active.content}</div>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}
